import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent, StandardEvents } from '@shopify/events';

const VARIANT_RESPONSE_CACHE_TTL = 30000;
const VARIANT_FETCH_DEBOUNCE = 120;
const VARIANT_REQUEST_TIMEOUT = 10000;

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {number} */
  #requestGeneration = 0;

  /** @type {Map<string, {text: string, expiresAt: number}>} */
  #responseCache = new Map();

  /** @type {string[]} */
  #committedOptionValueIds = [];

  /** @type {Map<string, HTMLImageElement>} */
  #preloadedMedia = new Map();

  /** @type {number | undefined} */
  #preloadTimeout;

  /** @type {number[]} */
  #staggeredPreloadTimeouts = [];

  /** @type {number | undefined} */
  #selectionFetchTimeout;

  /** @type {Array<Record<string, any>> | null} */
  #variantMatrix = null;

  #onChange = (/** @type {Event} */ event) => this.variantChanged(event);

  #onCartUpdate = () => this.#responseCache.clear();

  #onOptionIntent = (/** @type {Event} */ event) => {
    if (!(event.target instanceof Element)) return;

    const option =
      event.target instanceof HTMLInputElement
        ? event.target
        : event.target.closest('label')?.querySelector('input[data-option-media-url]');

    if (option instanceof HTMLInputElement) this.#preloadOptionMedia(option, 'high');
  };

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.#syncRadioState();
    this.#committedOptionValueIds = this.selectedOptionsValues;
    this.addEventListener('change', this.#onChange);
    this.addEventListener('pointerdown', this.#onOptionIntent, { passive: true });
    this.addEventListener('pointerover', this.#onOptionIntent, { passive: true });
    this.addEventListener('focusin', this.#onOptionIntent);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.addEventListener('pagehide', this.#resetPendingSelection);
    window.addEventListener('pageshow', this.#resetPendingSelection);
    this.#scheduleVariantMediaPreload();
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('pointerdown', this.#onOptionIntent);
    this.removeEventListener('pointerover', this.#onOptionIntent);
    this.removeEventListener('focusin', this.#onOptionIntent);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.removeEventListener('pagehide', this.#resetPendingSelection);
    window.removeEventListener('pageshow', this.#resetPendingSelection);
    if (this.#selectionFetchTimeout) window.clearTimeout(this.#selectionFetchTimeout);
    if (this.#preloadTimeout) window.clearTimeout(this.#preloadTimeout);
    for (const timeout of this.#staggeredPreloadTimeouts) window.clearTimeout(timeout);
    this.#staggeredPreloadTimeouts = [];
    this.#preloadedMedia.clear();
    this.#resizeObserver.disconnect();
  }

  #syncRadioState() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    this.#radios = [];
    this.#checkedIndices = [];

    fieldsets.forEach((fieldset) => {
      const radios = /** @type {HTMLInputElement[]} */ (Array.from(fieldset?.querySelectorAll('input') ?? []));
      const currentIndex = radios.findIndex((radio) => radio.checked);
      this.#radios.push(radios);
      this.#checkedIndices.push(currentIndex === -1 ? [] : [currentIndex]);
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    const localVariant = this.#resolveLocalVariant();
    const hasLocalMatrix = this.#getVariantMatrix().length > 0;
    this.#previewSelectedMedia(selectedOption, localVariant);
    this.#commitVariantIntent(selectedOption, localVariant, hasLocalMatrix);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;

    const optionValueId = selectedOption.dataset.optionValueId ?? '';
    const selectedOptionValueIds = this.selectedOptionsValues;
    const requestGeneration = ++this.#requestGeneration;
    const requestUrl = this.buildRequestUrl(selectedOption);

    // Commit the visible choice immediately, then collapse a burst of taps
    // into one canonical Shopify section refresh.
    this.#abortController?.abort();
    if (this.#selectionFetchTimeout) window.clearTimeout(this.#selectionFetchTimeout);
    this.#selectionFetchTimeout = window.setTimeout(() => {
      if (requestGeneration !== this.#requestGeneration || !this.isConnected) return;
      this.#selectionFetchTimeout = undefined;
      this.setAttribute('aria-busy', 'true');
      this.dataset.selectionPending = 'true';
      this.fetchUpdatedSection(
        requestUrl,
        morphElementSelector,
        optionValueId,
        requestGeneration,
        selectedOptionValueIds
      );
    }, VARIANT_FETCH_DEBOUNCE);

    const url = new URL(window.location.href);

    const variantId = localVariant?.id
      ? String(localVariant.id)
      : hasLocalMatrix
        ? null
        : selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Commits everything Shopify already rendered into the option control before
   * starting the section request. This keeps the selected variant, purchase
   * controls and sticky bar responsive even when section rendering takes
   * several seconds. The server response remains the authority for price,
   * inventory rules and the rest of the product markup.
   * @param {HTMLElement} selectedOption
   */
  #commitVariantIntent(selectedOption, localVariant = null, hasLocalMatrix = false) {
    const variantId = localVariant?.id
      ? String(localVariant.id)
      : hasLocalMatrix
        ? ''
        : selectedOption.dataset.variantId ?? '';
    const available = localVariant
      ? localVariant.available !== false
      : hasLocalMatrix
        ? false
        : selectedOption.dataset.optionAvailable !== 'false';
    const localMedia = localVariant?.featured_media;

    this.dispatchEvent(
      new CustomEvent('vorliva:variant-intent', {
        bubbles: true,
        composed: true,
        detail: {
          productId: this.dataset.productId ?? '',
          variantId,
          available,
          optionValueId: selectedOption.dataset.optionValueId ?? '',
          selectedOptions: this.getAllSelectedOptions(),
          mediaId: String(localMedia?.id ?? selectedOption.dataset.optionMediaId ?? ''),
          mediaUrl: localMedia?.preview_url ?? selectedOption.dataset.optionMediaUrl ?? '',
          mediaZoomUrl: localMedia?.zoom_url ?? '',
          mediaAlt: localMedia?.alt ?? '',
          variant: localVariant,
        },
      })
    );
  }

  /** @param {HTMLElement} selectedOption @param {Record<string, any> | null} [localVariant] */
  #previewSelectedMedia(selectedOption, localVariant = null) {
    const localMedia = localVariant?.featured_media;
    const mediaId = String(localMedia?.id ?? selectedOption.dataset.optionMediaId ?? '');
    if (!mediaId) return;

    const scope = this.closest('.shopify-section, dialog');
    const mediaGallery = /** @type {(HTMLElement & {previewVariantMedia?: (mediaId: string, mediaUrl?: string, zoomUrl?: string, mediaAlt?: string) => void}) | null} */ (scope?.querySelector('media-gallery') ?? null);
    mediaGallery?.previewVariantMedia?.(
      mediaId,
      localMedia?.preview_url ?? selectedOption.dataset.optionMediaUrl ?? '',
      localMedia?.zoom_url ?? '',
      localMedia?.alt ?? ''
    );
  }

  /** @param {HTMLElement} option @param {'high' | 'low'} [priority] */
  #preloadOptionMedia(option, priority = 'high') {
    const url = this.#resolveLocalVariant(option)?.featured_media?.preview_url ?? option.dataset.optionMediaUrl;
    if (!url) return;

    const existing = this.#preloadedMedia.get(url);
    if (existing) {
      if (priority === 'high') existing.fetchPriority = 'high';
      return;
    }

    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = priority;
    image.src = url;
    this.#preloadedMedia.set(url, image);
  }

  #scheduleVariantMediaPreload() {
    const connection = /** @type {{saveData?: boolean, effectiveType?: string} | undefined} */ (
      /** @type {any} */ (navigator).connection
    );
    if (connection?.saveData || connection?.effectiveType?.includes('2g')) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.#preloadTimeout = window.setTimeout(() => {
      const options = Array.from(this.querySelectorAll('[data-option-media-url]')).slice(0, 3);
      options.forEach((option, index) => {
        const timeout = window.setTimeout(() => {
          if (option instanceof HTMLElement && this.isConnected) this.#preloadOptionMedia(option, 'low');
        }, index * 80);
        this.#staggeredPreloadTimeouts.push(timeout);
      });
    }, 1800);
  }

  #getVariantMatrix() {
    if (this.#variantMatrix) return this.#variantMatrix;

    const source = this.querySelector('script[data-variant-matrix]')?.textContent;
    if (!source) return [];

    try {
      const parsed = JSON.parse(source);
      this.#variantMatrix = Array.isArray(parsed) ? parsed : [];
    } catch {
      this.#variantMatrix = [];
    }

    return this.#variantMatrix;
  }

  /** @param {HTMLElement | null} [prospectiveOption] */
  #resolveLocalVariant(prospectiveOption = null) {
    const selectedOptions = this.getAllSelectedOptions();
    if (prospectiveOption?.dataset.optionName) {
      const existing = selectedOptions.find((option) => option.name === prospectiveOption.dataset.optionName);
      if (existing) existing.value = prospectiveOption.value;
    }

    const selectedValues = selectedOptions.map((option) => option.value);
    if (selectedValues.length === 0) return null;

    return (
      this.#getVariantMatrix().find(
        (variant) =>
          Array.isArray(variant.options) &&
          variant.options.length === selectedValues.length &&
          variant.options.every((value, index) => value === selectedValues[index])
      ) ?? null
    );
  }

  #resetPendingSelection = () => {
    if (this.#selectionFetchTimeout) window.clearTimeout(this.#selectionFetchTimeout);
    this.#selectionFetchTimeout = undefined;
    this.#abortController?.abort();
    this.removeAttribute('aria-busy');
    delete this.dataset.selectionPending;
  };

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states. Clear every runtime state first so a value
          // selected two or more interactions ago cannot remain visually active.
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          radios.forEach((radio) => {
            const isCurrent = radio === target;
            radio.checked = isCurrent;
            radio.toggleAttribute('checked', isCurrent);
            radio.dataset.currentChecked = isCurrent ? 'true' : 'false';
            radio.dataset.previousChecked = 'false';
          });

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);

          const currentValue = fieldset.querySelector('.variant-option__swatch-value');
          if (currentValue) currentValue.textContent = target.value;
        }
      }
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        const isSelected = option === newSelectedOption;
        option.selected = isSelected;
        option.toggleAttribute('selected', isSelected);
      }
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    const productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    const requestUrl = new URL(productUrl || window.location.pathname, window.location.origin);
    this.#pendingRequestUrl = requestUrl.pathname;
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) requestUrl.searchParams.set('view', viewParamValue);
    else requestUrl.searchParams.delete('view');

    // A stale ?variant= query can override option_values on some product URLs.
    requestUrl.searchParams.delete('variant');

    if (this.selectedOptionsValues.length && !source) {
      requestUrl.searchParams.set('option_values', this.selectedOptionsValues.join(','));
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        requestUrl.searchParams.set('option_values', sourceSelectedOptionsValues.join(','));
      } else {
        requestUrl.searchParams.set('option_values', selectedOption.dataset.optionValueId || '');
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      requestUrl.searchParams.set('section_id', SECTION_ID_MAP[closestSectionId] || '');
    } else if (this.dataset.sectionId && !selectedOption.dataset.connectedProductUrl) {
      // The previous implementation fetched the entire product page for every
      // regular variant click. A single-section response keeps the same Liquid
      // context while removing layout, header, footer and app markup from the
      // critical interaction request.
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);
    }

    return requestUrl.toString();
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {string} [optionValueId] - The selected option value ID for event detail.
   * @param {number} [requestGeneration] - Monotonic request token used to ignore stale responses.
   * @param {string[]} [selectedOptionValueIds] - Ordered option value IDs captured at interaction time.
   */
  fetchUpdatedSection(
    requestUrl,
    morphElementSelector,
    optionValueId = '',
    requestGeneration = this.#requestGeneration,
    selectedOptionValueIds = this.selectedOptionsValues
  ) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
        },
        promise: deferredEventPromise.promise,
      })
    );

    this.#fetchResponseText(requestUrl, this.#abortController.signal)
      .then((responseText) => {
        if (requestGeneration !== this.#requestGeneration) return;
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          throw new Error('Variant response did not include product data');
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html, selectedOptionValueIds);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (optionValueId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            this.#committedOptionValueIds = [...selectedOptionValueIds];
            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: optionValueId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        this.#committedOptionValueIds = [...selectedOptionValueIds];
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: optionValueId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name !== 'AbortError') {
          // The embedded matrix is exact for this product, so a slow Section
          // Rendering response must not undo a valid local choice. Only the
          // legacy no-matrix path needs to roll back.
          if (requestGeneration === this.#requestGeneration && this.#getVariantMatrix().length === 0) {
            this.#restoreCommittedSelection();
          }
          console.error(error);
        }
      })
      .finally(() => {
        if (requestGeneration !== this.#requestGeneration) return;
        this.removeAttribute('aria-busy');
        delete this.dataset.selectionPending;
      });
  }

  /**
   * Keeps successful section responses in a small session cache so switching
   * back to a previously viewed color does not repeat the network round trip.
   * @param {string} requestUrl
   * @param {AbortSignal} signal
   */
  async #fetchResponseText(requestUrl, signal) {
    const cached = this.#responseCache.get(requestUrl);
    if (cached && cached.expiresAt > Date.now()) return cached.text;
    if (cached) this.#responseCache.delete(requestUrl);

    const controller = new AbortController();
    const abortFromCaller = () => controller.abort();
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', abortFromCaller, { once: true });
    const timeout = window.setTimeout(() => controller.abort(), VARIANT_REQUEST_TIMEOUT);

    let responseText;
    try {
      const response = await fetch(requestUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
      responseText = await response.text();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError' && !signal.aborted) {
        throw new Error('Variant update timed out. Please try again.');
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
      signal.removeEventListener('abort', abortFromCaller);
    }

    this.#responseCache.set(requestUrl, {
      text: responseText,
      expiresAt: Date.now() + VARIANT_RESPONSE_CACHE_TTL,
    });

    if (this.#responseCache.size > 12) {
      const oldestKey = this.#responseCache.keys().next().value;
      if (oldestKey) this.#responseCache.delete(oldestKey);
    }

    return responseText;
  }

  #restoreCommittedSelection() {
    if (this.#committedOptionValueIds.length === 0) return;

    this.#restoreSelectedOptions(this.#committedOptionValueIds);
    this.#syncRadioState();
    this.updateVariantPickerCss();

    const selected = this.#getSelectedOptionElements().find((option) => option.dataset.optionMediaId);
    if (selected instanceof HTMLElement) {
      const localVariant = this.#resolveLocalVariant();
      const hasLocalMatrix = this.#getVariantMatrix().length > 0;
      this.#previewSelectedMedia(selected, localVariant);
      this.#commitVariantIntent(selected, localVariant, hasLocalMatrix);
    }

    const variantId = this.#getSelectedOptionElements().find((option) => option.dataset.variantId)?.dataset.variantId;
    if (this.dataset.templateProductMatch === 'true') {
      const url = new URL(window.location.href);
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @param {string[]} [selectedOptionValueIds] - Local selection that must survive the server morph.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml, selectedOptionValueIds = this.selectedOptionsValues) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });

    this.#variantMatrix = null;

    // Server HTML can momentarily carry the previous checked/defaultChecked
    // state. Re-apply the interaction-time selection in the same task so the
    // control never flashes or settles back on the old color.
    this.#syncRadioState();
    this.#restoreSelectedOptions(selectedOptionValueIds);
    this.#syncRadioState();
    this.updateVariantPickerCss();

    return newProduct;
  }

  /** @param {string[]} optionValueIds */
  #restoreSelectedOptions(optionValueIds) {
    for (const optionValueId of optionValueIds) {
      const option = Array.from(this.querySelectorAll('[data-option-value-id]')).find(
        (element) => element instanceof HTMLElement && element.dataset.optionValueId === optionValueId
      );

      if (option instanceof HTMLInputElement && option.type === 'radio') {
        this.updateSelectedOption(option);
      } else if (option instanceof HTMLOptionElement) {
        const select = option.closest('select');
        if (select) {
          select.value = option.value;
          this.updateSelectedOption(select);
        }
      }
    }
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.#getSelectedOptionElements()[0];

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    return this.#getSelectedOptionElements().flatMap((option) => {
      const name = option.dataset.optionName;
      return name ? [{ name, value: option.value }] : [];
    });
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    return this.#getSelectedOptionElements().map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Returns one selected value per product option in DOM/Shopify option order.
   * Single-value options are represented by a hidden metadata input so they
   * are not dropped from the option_values request.
   * @returns {(HTMLInputElement | HTMLOptionElement)[]}
   */
  #getSelectedOptionElements() {
    /** @type {(HTMLInputElement | HTMLOptionElement)[]} */
    const selectedOptions = [];

    for (const container of this.querySelectorAll('form > .variant-option')) {
      const selected = container.querySelector(
        'select option:checked, input[type="radio"]:checked, input[data-single-option-value]'
      );

      if (selected instanceof HTMLInputElement || selected instanceof HTMLOptionElement) {
        selectedOptions.push(selected);
      }
    }

    return selectedOptions;
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
